import React, { useState, useEffect } from 'react';
import { Grid, Typography, Box, IconButton, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SelectCollectbyOffice from './SelectCollectbyOffice';
import { Office } from '../../models/office';
import { Collect } from '../../models/collect';

interface Props {
  offices: Office[];
  collectsByOffice: Record<string, Collect[]>;
  initialSelections: { office_id: string; selectedCollects: string[] }[];
  onChange: (data: { office_id: string; selectedCollects: string[] }[]) => void;
}

const SelectUserCollectsByOffice: React.FC<Props> = ({
  offices,
  collectsByOffice,
  initialSelections,
  onChange
}) => {
  const [selections, setSelections] = useState(initialSelections);
  useEffect(() => {
    onChange(selections);
  }, [selections]);

  const handleOfficeChange = (index: number, office_id: string) => {
    const updated = [...selections];
    updated[index].office_id = office_id;
    updated[index].selectedCollects = [];
    setSelections(updated);
  };

  const handleCollectChange = (index: number, collects: string[]) => {
    const updated = [...selections];
    updated[index].selectedCollects = collects;
    setSelections(updated);
  };

  const handleSelectAll = (index: number, checked: boolean, collects: Collect[]) => {
    const updated = [...selections];
    updated[index].selectedCollects = checked ? collects.map(c => c.id.toString()) : [];
    setSelections(updated);
  };

  const handleRemove = (index: number) => {
    const updated = [...selections];
    updated.splice(index, 1);
    setSelections(updated);
  };

  const handleAddOffice = () => {
    const last = selections[selections.length - 1];
    if (last && (!last.office_id || last.selectedCollects.length === 0)) {
      alert('Debe completar la oficina actual antes de agregar otra.');
      return;
    }

    setSelections([...selections, { office_id: '', selectedCollects: [] }]);
  };

  const selectedOfficeIds = selections.map(s => s.office_id).filter(Boolean);
  const getAvailableOffices = (currentOfficeId: string) =>
    offices.filter(
      (o) =>
        o.id.toString() === currentOfficeId ||
        !selectedOfficeIds.includes(o.id.toString())
    );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Oficinas y Cobros Asignados
        </Typography>
      </Grid>

      {selections.map((selection, index) => {
        const officeOptions = getAvailableOffices(selection.office_id);
        const collectsOffice = collectsByOffice[selection.office_id] || [];

        return (
          <Grid item xs={12} key={`${selection.office_id || 'new'}-${index}`}>
            <Box display="flex" alignItems="flex-start" gap={2}>
              <Box flex={1}>
                <SelectCollectbyOffice
                  office_id={selection.office_id}
                  setOffice_id={(val) => handleOfficeChange(index, val)}
                  offices={officeOptions}
                  collectsOffice={collectsOffice}
                  selectedCollects={selection.selectedCollects}
                  setSelectedCollects={(vals) => handleCollectChange(index, vals)}
                  selectAll={
                    selection.selectedCollects.length > 0 &&
                    selection.selectedCollects.length === collectsOffice.length
                  }
                  setSelectAll={() => {}}
                  handleCheckboxChange={(e) => {
                    const { value, checked } = e.target;
                    const updated = [...selection.selectedCollects];
                    if (checked && !updated.includes(value)) updated.push(value);
                    else if (!checked) updated.splice(updated.indexOf(value), 1);
                    handleCollectChange(index, updated);
                  }}
                  handleSelectAll={(e) =>
                    handleSelectAll(index, e.target.checked, collectsOffice)
                  }
                />
              </Box>
              <IconButton onClick={() => handleRemove(index)} color="error" sx={{ mt: 1 }}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Grid>
        );
      })}

      {offices.length > selections.length && (
        <Grid item xs={12}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddOffice}>
            Agregar Oficina
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default SelectUserCollectsByOffice;
